/**
 * AmbientAudioEngine - Procedural Environmental BGM & Ambience Synthesizer
 * 100% Procedural synthesis using Web Audio API without external audio files.
 * 
 * Features:
 * - Four Seasons x Day/Night Nature Ambience:
 *   * Spring: Day = Birds Chirping (새 지저귐, Freq: 3), Night = Crickets (풀벌레, Freq: 3)
 *   * Summer: Day = Cicadas (매미, Freq: 4), Night = Frogs Croaking (개구리, Freq: 4)
 *   * Autumn: Day = Leaves Rustling (낙엽 바스락, Freq: 2), Night = Crickets (풀벌레, Freq: 2)
 *   * Winter: Day & Night = Chilly Howling Wind Loop (매서운 겨울 바람 지속 노이즈)
 * - Dynamic Weather Sound Effects:
 *   * Rain & Thunderstorm: Continuous rainfall noise ambience loop
 *   * Thunderstorm: Sharp lightning crack & deep resonant sub-bass thunder rumble
 * - Dungeon/Indoor: Eerie low-frequency crypt drone (65Hz) & cave water drips
 * 
 * Strict constraints:
 * - Outdoor field soundscapes ONLY play in the overworld field.
 * - Automatically muted/faded out when entering dungeons or indoors.
 */

import { SeasonType, TimeOfDay, WeatherType } from '../world/EnvironmentSystem';
import { BiomeType, getBiomeAt } from '../world/BiomeSystem';

export class AmbientAudioEngine {
  private ctx: AudioContext | null = null;
  private masterGain: GainNode | null = null;
  private ambienceGain: GainNode | null = null;

  // Continuous soundscape loops
  private windNode: AudioBufferSourceNode | null = null;
  private windGain: GainNode | null = null;
  private windFilter: BiquadFilterNode | null = null;

  private rainNode: AudioBufferSourceNode | null = null;
  private rainGain: GainNode | null = null;
  private rainFilter: BiquadFilterNode | null = null;

  private dungeonDroneNode: OscillatorNode | null = null;
  private dungeonGain: GainNode | null = null;

  // Continuous authentic cicada loop from YouTube audio asset (NaIudP0o8LM)
  private cicadaBuffer: AudioBuffer | null = null;
  private cicadaNode: AudioBufferSourceNode | null = null;
  private cicadaGain: GainNode | null = null;
  private isCicadaLoading = false;

  // Timers for episodic creature & environmental sounds
  private birdTimer = 0;
  private cicadaTimer = 0;
  private frogTimer = 0;
  private cricketTimer = 0;
  private leafTimer = 0;
  private waterDripTimer = 0;

  // Cached state for inspection / testing
  private currentSeason: SeasonType = 'summer';
  private currentTimeOfDay: TimeOfDay = 'day';
  private currentWeather: WeatherType = 'clear';
  private currentBiome: BiomeType = 'plains';
  private isMuted = false;

  public init(ctx: AudioContext, parentGain: GainNode): void {
    this.ctx = ctx;
    this.masterGain = parentGain;

    this.ambienceGain = this.ctx.createGain();
    this.ambienceGain.gain.setValueAtTime(0.35, this.ctx.currentTime);
    this.ambienceGain.connect(this.masterGain);

    this.setupWindAmbience();
    this.setupRainAmbience();
    this.setupDungeonDrone();
    this.setupCicadaAmbience();

    // Default to mute until explicitly activated in-world
    this.setMute(true);
  }

  public setMute(muted: boolean): void {
    this.isMuted = muted;
    if (this.ambienceGain && this.ctx) {
      this.ambienceGain.gain.setTargetAtTime(muted ? 0 : 0.35, this.ctx.currentTime, 0.05);
    }
    // Also silence active loop sub-gains immediately if muted
    if (muted && this.ctx) {
      if (this.windGain) this.windGain.gain.setTargetAtTime(0, this.ctx.currentTime, 0.05);
      if (this.rainGain) this.rainGain.gain.setTargetAtTime(0, this.ctx.currentTime, 0.05);
      if (this.dungeonGain) this.dungeonGain.gain.setTargetAtTime(0, this.ctx.currentTime, 0.05);
      if (this.cicadaGain) this.cicadaGain.gain.setTargetAtTime(0, this.ctx.currentTime, 0.05);
    }
  }

  public getCurrentState() {
    return {
      season: this.currentSeason,
      timeOfDay: this.currentTimeOfDay,
      weather: this.currentWeather,
      biome: this.currentBiome,
      isMuted: this.isMuted,
    };
  }

  /**
   * Continuous procedural pink noise wind generator
   */
  private setupWindAmbience(): void {
    if (!this.ctx || !this.ambienceGain) return;

    // Generate 4-second looping pink noise buffer for natural wind
    const bufferSize = this.ctx.sampleRate * 4;
    const noiseBuffer = this.ctx.createBuffer(1, bufferSize, this.ctx.sampleRate);
    const output = noiseBuffer.getChannelData(0);

    let b0 = 0, b1 = 0, b2 = 0, b3 = 0, b4 = 0, b5 = 0, b6 = 0;
    for (let i = 0; i < bufferSize; i++) {
      const white = Math.random() * 2 - 1;
      b0 = 0.99886 * b0 + white * 0.0555179;
      b1 = 0.99332 * b1 + white * 0.0750759;
      b2 = 0.96900 * b2 + white * 0.1538520;
      b3 = 0.86650 * b3 + white * 0.3104856;
      b4 = 0.55000 * b4 + white * 0.5329522;
      b5 = -0.7616 * b5 - white * 0.0168980;
      output[i] = (b0 + b1 + b2 + b3 + b4 + b5 + b6 + white * 0.5362) * 0.08;
      b6 = white * 0.115926;
    }

    this.windNode = this.ctx.createBufferSource();
    this.windNode.buffer = noiseBuffer;
    this.windNode.loop = true;

    this.windFilter = this.ctx.createBiquadFilter();
    this.windFilter.type = 'bandpass';
    this.windFilter.frequency.setValueAtTime(450, this.ctx.currentTime);
    this.windFilter.Q.setValueAtTime(1.8, this.ctx.currentTime);

    this.windGain = this.ctx.createGain();
    this.windGain.gain.setValueAtTime(0, this.ctx.currentTime); // Initially silent

    this.windNode.connect(this.windFilter);
    this.windFilter.connect(this.windGain);
    this.windGain.connect(this.ambienceGain);

    this.windNode.start();
  }

  /**
   * Continuous procedural rainfall noise ambience loop
   */
  private setupRainAmbience(): void {
    if (!this.ctx || !this.ambienceGain) return;

    // Generate 4-second looping filtered noise buffer for natural rain texture
    const bufferSize = this.ctx.sampleRate * 4;
    const rainBuffer = this.ctx.createBuffer(1, bufferSize, this.ctx.sampleRate);
    const data = rainBuffer.getChannelData(0);

    let lastVal = 0;
    for (let i = 0; i < bufferSize; i++) {
      // Gentle soft white-pink noise mix for rain droplets
      const white = Math.random() * 2 - 1;
      lastVal = (lastVal + 0.025 * white) / 1.025;
      data[i] = (lastVal * 3.5 + white * 0.25) * 0.06;
    }

    this.rainNode = this.ctx.createBufferSource();
    this.rainNode.buffer = rainBuffer;
    this.rainNode.loop = true;

    this.rainFilter = this.ctx.createBiquadFilter();
    this.rainFilter.type = 'lowpass';
    this.rainFilter.frequency.setValueAtTime(1600, this.ctx.currentTime);

    this.rainGain = this.ctx.createGain();
    this.rainGain.gain.setValueAtTime(0, this.ctx.currentTime); // Initially silent

    this.rainNode.connect(this.rainFilter);
    this.rainFilter.connect(this.rainGain);
    this.rainGain.connect(this.ambienceGain);

    this.rainNode.start();
  }

  /**
   * Continuous eerie underground crypt drone
   */
  private setupDungeonDrone(): void {
    if (!this.ctx || !this.ambienceGain) return;

    this.dungeonDroneNode = this.ctx.createOscillator();
    this.dungeonDroneNode.type = 'sine';
    this.dungeonDroneNode.frequency.setValueAtTime(65, this.ctx.currentTime); // Deep 65Hz hum

    const droneFilter = this.ctx.createBiquadFilter();
    droneFilter.type = 'lowpass';
    droneFilter.frequency.setValueAtTime(140, this.ctx.currentTime);

    this.dungeonGain = this.ctx.createGain();
    this.dungeonGain.gain.setValueAtTime(0, this.ctx.currentTime); // Initially silent

    this.dungeonDroneNode.connect(droneFilter);
    droneFilter.connect(this.dungeonGain);
    this.dungeonGain.connect(this.ambienceGain);

    this.dungeonDroneNode.start();
  }

  /**
   * Continuous authentic Korean summer cicada (참매미) ambient loop from YouTube asset
   */
  private setupCicadaAmbience(): void {
    if (!this.ctx || !this.ambienceGain || this.cicadaGain) return;

    this.cicadaGain = this.ctx.createGain();
    this.cicadaGain.gain.setValueAtTime(0, this.ctx.currentTime); // Initially silent
    this.cicadaGain.connect(this.ambienceGain);

    if (this.cicadaBuffer || this.isCicadaLoading) return;
    this.isCicadaLoading = true;

    // Fetch the 65-second seamless loop extracted from YouTube (NaIudP0o8LM)
    if (typeof window !== 'undefined' && typeof fetch === 'function') {
      fetch('/sounds/ambient/cicada_summer.mp3')
        .then((res) => {
          if (!res.ok) throw new Error(`HTTP ${res.status}`);
          return res.arrayBuffer();
        })
        .then((arrayBuffer) => {
          if (!this.ctx) return;
          return this.ctx.decodeAudioData(arrayBuffer);
        })
        .then((decoded) => {
          if (!decoded || !this.ctx || !this.cicadaGain) return;
          this.cicadaBuffer = decoded;
          this.startCicadaLoop();
        })
        .catch((err) => {
          console.warn('[AmbientAudioEngine] cicada_summer.mp3 asset load skipped or failed; using procedural 참매미 synthesis', err);
        })
        .finally(() => {
          this.isCicadaLoading = false;
        });
    }
  }

  private startCicadaLoop(): void {
    if (!this.ctx || !this.cicadaBuffer || !this.cicadaGain || this.cicadaNode) return;
    try {
      this.cicadaNode = this.ctx.createBufferSource();
      this.cicadaNode.buffer = this.cicadaBuffer;
      this.cicadaNode.loop = true;
      this.cicadaNode.connect(this.cicadaGain);
      this.cicadaNode.start();
    } catch (e) {
      console.warn('[AmbientAudioEngine] Failed to start cicada loop node', e);
    }
  }

  /**
   * Main audio loop tick called every frame
   */
  public update(
    dt: number,
    playerX: number,
    playerY: number,
    season: SeasonType,
    timeOfDay: TimeOfDay = 'day',
    weather: WeatherType = 'clear'
  ): void {
    if (!this.ctx || this.isMuted) return;

    this.currentSeason = season;
    this.currentTimeOfDay = timeOfDay;
    this.currentWeather = weather;
    this.currentBiome = getBiomeAt(playerX, playerY);
    const isIndoorDungeon = this.currentBiome === 'dungeon';

    // 1. Manage Dungeon Drone vs Outdoor Ambience
    if (isIndoorDungeon) {
      // Fade IN dungeon drone
      if (this.dungeonGain) {
        this.dungeonGain.gain.setTargetAtTime(0.25, this.ctx.currentTime, 0.4);
      }
      // Fade OUT outdoor wind, rain, and cicada loop
      if (this.windGain) {
        this.windGain.gain.setTargetAtTime(0, this.ctx.currentTime, 0.3);
      }
      if (this.rainGain) {
        this.rainGain.gain.setTargetAtTime(0, this.ctx.currentTime, 0.3);
      }
      if (this.cicadaGain) {
        this.cicadaGain.gain.setTargetAtTime(0, this.ctx.currentTime, 0.3);
      }

      // Cave water drips
      this.waterDripTimer -= dt;
      if (this.waterDripTimer <= 0) {
        this.waterDripTimer = 2.5 + Math.random() * 4.0;
        this.synthesizeWaterDrip();
      }
      return; // Do NOT play outdoor field creature/weather sounds in dungeon!
    } else {
      // In outdoor field: Fade OUT dungeon drone
      if (this.dungeonGain) {
        this.dungeonGain.gain.setTargetAtTime(0, this.ctx.currentTime, 0.4);
      }
    }

    // 2. Continuous Weather Rainfall Ambience (Rain & Thunderstorm)
    if (this.rainGain && this.rainFilter) {
      if (weather === 'rain') {
        // Calming gentle rainfall
        this.rainGain.gain.setTargetAtTime(0.18, this.ctx.currentTime, 0.4);
        this.rainFilter.frequency.setTargetAtTime(1400, this.ctx.currentTime, 0.3);
      } else if (weather === 'thunderstorm') {
        // Heavy, intense downpour
        this.rainGain.gain.setTargetAtTime(0.28, this.ctx.currentTime, 0.4);
        this.rainFilter.frequency.setTargetAtTime(2200, this.ctx.currentTime, 0.3);
      } else {
        // Clear, Snow, Fog: Fade OUT rain
        this.rainGain.gain.setTargetAtTime(0, this.ctx.currentTime, 0.4);
      }
    }

    // 3. Winter Chilly Wind Loop (Day & Night)
    if (season === 'winter') {
      if (this.windGain && this.windFilter) {
        this.windGain.gain.setTargetAtTime(0.35, this.ctx.currentTime, 0.4);
        // Modulate wind howling pitch
        const freq = 400 + Math.sin(performance.now() * 0.0012) * 220;
        this.windFilter.frequency.setTargetAtTime(freq, this.ctx.currentTime, 0.2);
      }
    } else {
      // Fade OUT wind in Spring, Summer, Autumn
      if (this.windGain) {
        this.windGain.gain.setTargetAtTime(0, this.ctx.currentTime, 0.4);
      }
    }

    // 3-1. Summer Day Cicada Loop (From YouTube recording NaIudP0o8LM)
    const isOutdoorSummerDay = season === 'summer' && timeOfDay === 'day' && weather !== 'rain' && weather !== 'thunderstorm';
    if (this.cicadaGain && this.ctx) {
      if (isOutdoorSummerDay && this.cicadaBuffer && this.cicadaNode) {
        this.cicadaGain.gain.setTargetAtTime(0.30, this.ctx.currentTime, 0.5);
      } else {
        this.cicadaGain.gain.setTargetAtTime(0, this.ctx.currentTime, 0.4);
      }
    }

    // 4. Outdoor Field Ambience by Season & Day/Night
    // Frequency Scale 1~5 (5: highest freq, 3: baseline cricket ~2.0-3.5s)
    // CRITICAL: Dawn (새벽) and Dusk (석양/노을) have NO creature ambient BGM (Silence).
    // Episodic creature sounds play ONLY during 'day' and 'night'!
    if (timeOfDay === 'day') {
      if (season === 'spring') {
        // Spring Day: Birds Chirping (새들 짹짹거리며 지저귀는 소리, 빈도 3 -> 2.0~3.5s)
        this.birdTimer -= dt;
        if (this.birdTimer <= 0) {
          this.birdTimer = 2.0 + Math.random() * 1.5;
          this.synthesizeBirdChirp();
        }
      } else if (season === 'summer') {
        // Summer Day: Cicadas (참매미 소리)
        // If mp3 buffer is loaded, authentic MP3 loop plays continuous chorus.
        // synthesizeCicada() is only a fallback when mp3 is not loaded.
        if (!this.cicadaBuffer) {
          this.cicadaTimer -= dt;
          if (this.cicadaTimer <= 0) {
            this.cicadaTimer = 14.0 + Math.random() * 6.0;
            this.synthesizeCicada();
          }
        }
      } else if (season === 'autumn') {
        // Autumn Day: Leaves Rustling (낙엽 바스락 소리, 빈도 2 -> 3.5~5.5s)
        this.leafTimer -= dt;
        if (this.leafTimer <= 0) {
          this.leafTimer = 3.5 + Math.random() * 2.0;
          this.synthesizeLeafRustle();
        }
      }
    } else if (timeOfDay === 'night') {
      if (season === 'spring') {
        // Spring Night: Crickets (풀벌레 소리, 빈도 3 -> 2.0~3.5s)
        this.cricketTimer -= dt;
        if (this.cricketTimer <= 0) {
          this.cricketTimer = 2.0 + Math.random() * 1.5;
          this.synthesizeCricketChirp();
        }
      } else if (season === 'summer') {
        // Summer Night: Frogs Croaking (개구리 소리, 빈도 4 -> 1.2~2.2s)
        this.frogTimer -= dt;
        if (this.frogTimer <= 0) {
          this.frogTimer = 1.2 + Math.random() * 1.0;
          this.synthesizeFrogCroak();
        }
      } else if (season === 'autumn') {
        // Autumn Night: Crickets (풀벌레 소리, 빈도 2 -> 3.5~5.5s)
        this.cricketTimer -= dt;
        if (this.cricketTimer <= 0) {
          this.cricketTimer = 3.5 + Math.random() * 2.0;
          this.synthesizeCricketChirp();
        }
      }
    }
    // When timeOfDay is 'dawn' or 'dusk': Complete silence for creature ambients!
    // Winter: Handled in Step 3 above (Wind loop runs day and night)
  }

  /**
   * Synthesize episodic bird chirping (봄 낮 새 지저귐 소리)
   * High-pitch FM chirps with double-phrase glissando (2400Hz - 3800Hz)
   */
  public synthesizeBirdChirp(): void {
    if (!this.ctx || !this.ambienceGain || this.isMuted) return;

    const t = this.ctx.currentTime;
    const osc = this.ctx.createOscillator();
    const gain = this.ctx.createGain();

    osc.type = 'sine';
    const baseFreq = 2600 + Math.random() * 500;

    // Pitch envelope: quick upward sweep, tiny dip, then second higher chirp
    osc.frequency.setValueAtTime(baseFreq, t);
    osc.frequency.exponentialRampToValueAtTime(baseFreq * 1.35, t + 0.05);
    osc.frequency.setValueAtTime(baseFreq * 0.95, t + 0.09);
    osc.frequency.exponentialRampToValueAtTime(baseFreq * 1.45, t + 0.16);

    // Amplitude envelope: 2 distinct chirps
    gain.gain.setValueAtTime(0, t);
    gain.gain.linearRampToValueAtTime(0.045, t + 0.03);
    gain.gain.linearRampToValueAtTime(0.005, t + 0.07);
    gain.gain.linearRampToValueAtTime(0.05, t + 0.12);
    gain.gain.exponentialRampToValueAtTime(0.0001, t + 0.22);

    osc.connect(gain);
    gain.connect(this.ambienceGain);

    osc.start(t);
    osc.stop(t + 0.24);
  }

  /**
   * Synthesize episodic authentic Korean 참매미 (Cham-maemi) call
   * Based on Korean nature recording (YouTube: NaIudP0o8LM).
   * Replaces the former rattlesnake-like white-noise burst with an authentic multi-stage tonal insect call:
   * 1) 3 slow intro pulses: "맴... 맴... 맴..."
   * 2) 4 rapid accelerated pulses: "맴! 맴! 맴! 맴!"
   * 3) Climax sustained song: "미이이이이이이이이이이..." with 12Hz timbal vibration
   * 4) Outro decay with pitch slide down: "쓰으으으... 뚝"
   */
  public synthesizeCicada(): void {
    if (!this.ctx || !this.ambienceGain || this.isMuted) return;

    const t = this.ctx.currentTime;
    const duration = 11.0;

    // Carrier 1: Primary timbal resonance (5600Hz ~ 5850Hz)
    const osc1 = this.ctx.createOscillator();
    osc1.type = 'sawtooth';
    osc1.frequency.setValueAtTime(5600, t);
    osc1.frequency.linearRampToValueAtTime(5850, t + 4.0);
    osc1.frequency.linearRampToValueAtTime(5900, t + 9.5);
    osc1.frequency.linearRampToValueAtTime(5200, t + duration); // Downward pitch slide

    // Carrier 2: Harmonic timbal overtone (6400Hz ~ 6600Hz)
    const osc2 = this.ctx.createOscillator();
    osc2.type = 'sine';
    osc2.frequency.setValueAtTime(6400, t);
    osc2.frequency.linearRampToValueAtTime(6600, t + 9.5);
    osc2.frequency.linearRampToValueAtTime(5800, t + duration);

    // Resonant bandpass filter (5800Hz, Q: 6.5)
    const filter = this.ctx.createBiquadFilter();
    filter.type = 'bandpass';
    filter.frequency.setValueAtTime(5800, t);
    filter.Q.setValueAtTime(6.5, t);

    // 12Hz timbal vibration LFO for sustained climax
    const lfo = this.ctx.createOscillator();
    lfo.type = 'sine';
    lfo.frequency.setValueAtTime(12.0, t);

    const lfoGain = this.ctx.createGain();
    lfoGain.gain.setValueAtTime(0, t);
    lfoGain.gain.setValueAtTime(0, t + 4.5);
    lfoGain.gain.linearRampToValueAtTime(0.35, t + 5.2);
    lfoGain.gain.setValueAtTime(0.35, t + 9.0);
    lfoGain.gain.linearRampToValueAtTime(0, t + 9.8);

    const amGain = this.ctx.createGain();
    amGain.gain.setValueAtTime(0.65, t);
    lfo.connect(lfoGain);
    lfoGain.connect(amGain.gain);

    // Master Amplitude Envelope: Korean 참매미 rhythmic cadence
    const envGain = this.ctx.createGain();
    envGain.gain.setValueAtTime(0, t);

    // Stage 1: 3 Slow Intro Pulses ("맴... 맴... 맴...")
    // Pulse 1 (0.2s - 0.85s)
    envGain.gain.setValueAtTime(0, t + 0.15);
    envGain.gain.linearRampToValueAtTime(0.045, t + 0.45);
    envGain.gain.linearRampToValueAtTime(0.005, t + 0.85);
    // Pulse 2 (1.05s - 1.75s)
    envGain.gain.linearRampToValueAtTime(0.065, t + 1.35);
    envGain.gain.linearRampToValueAtTime(0.008, t + 1.75);
    // Pulse 3 (1.95s - 2.65s)
    envGain.gain.linearRampToValueAtTime(0.080, t + 2.25);
    envGain.gain.linearRampToValueAtTime(0.010, t + 2.65);

    // Stage 2: 4 Accelerated Pulses ("맴! 맴! 맴! 맴!")
    // Pulse 4 (2.85s - 3.25s)
    envGain.gain.linearRampToValueAtTime(0.095, t + 3.05);
    envGain.gain.linearRampToValueAtTime(0.015, t + 3.25);
    // Pulse 5 (3.40s - 3.75s)
    envGain.gain.linearRampToValueAtTime(0.110, t + 3.55);
    envGain.gain.linearRampToValueAtTime(0.020, t + 3.75);
    // Pulse 6 (3.90s - 4.25s)
    envGain.gain.linearRampToValueAtTime(0.125, t + 4.05);
    envGain.gain.linearRampToValueAtTime(0.025, t + 4.25);
    // Pulse 7 (4.35s - 4.65s)
    envGain.gain.linearRampToValueAtTime(0.135, t + 4.50);
    envGain.gain.linearRampToValueAtTime(0.040, t + 4.65);

    // Stage 3: Climax Sustained Song ("미이이이이이이이이이이이이...") (4.8s - 9.5s)
    envGain.gain.linearRampToValueAtTime(0.160, t + 5.5);
    envGain.gain.setValueAtTime(0.160, t + 8.5);
    envGain.gain.linearRampToValueAtTime(0.120, t + 9.5);

    // Stage 4: Outro Decay ("쓰으으으... 뚝") (9.5s - 11.0s)
    envGain.gain.linearRampToValueAtTime(0.035, t + 10.3);
    envGain.gain.exponentialRampToValueAtTime(0.0001, t + duration);

    // Node connections
    osc1.connect(filter);
    osc2.connect(filter);
    filter.connect(amGain);
    amGain.connect(envGain);
    envGain.connect(this.ambienceGain);

    osc1.start(t);
    osc2.start(t);
    lfo.start(t);

    osc1.stop(t + duration);
    osc2.stop(t + duration);
    lfo.stop(t + duration);
  }

  /**
   * Synthesize realistic frog croak (여름 밤 개구리 소리: 개골개골)
   * Deep resonant formant (180Hz ~ 240Hz with 750Hz bandpass throat resonance)
   */
  public synthesizeFrogCroak(): void {
    if (!this.ctx || !this.ambienceGain || this.isMuted) return;

    const t = this.ctx.currentTime;
    const osc = this.ctx.createOscillator();
    const filter = this.ctx.createBiquadFilter();
    const gain = this.ctx.createGain();

    osc.type = 'sawtooth';
    const baseFreq = 180 + Math.random() * 60;
    osc.frequency.setValueAtTime(baseFreq, t);
    osc.frequency.exponentialRampToValueAtTime(baseFreq * 0.75, t + 0.22);

    filter.type = 'bandpass';
    filter.frequency.setValueAtTime(750, t);
    filter.Q.setValueAtTime(4.0, t); // Throat resonance

    gain.gain.setValueAtTime(0, t);
    gain.gain.linearRampToValueAtTime(0.08, t + 0.04);
    gain.gain.linearRampToValueAtTime(0.04, t + 0.10);
    gain.gain.linearRampToValueAtTime(0.07, t + 0.14);
    gain.gain.linearRampToValueAtTime(0.0001, t + 0.25);

    osc.connect(filter);
    filter.connect(gain);
    gain.connect(this.ambienceGain);

    osc.start(t);
    osc.stop(t + 0.28);
  }

  /**
   * Synthesize episodic cricket chirping burst (봄 밤 & 가을 밤 풀벌레 소리)
   * High frequency FM pulse (4800Hz - 5400Hz)
   */
  public synthesizeCricketChirp(): void {
    if (!this.ctx || !this.ambienceGain || this.isMuted) return;

    const t = this.ctx.currentTime;
    const osc = this.ctx.createOscillator();
    const gain = this.ctx.createGain();

    osc.type = 'triangle';
    const baseFreq = 4800 + Math.random() * 600;
    osc.frequency.setValueAtTime(baseFreq, t);

    // Chirp amplitude envelope (3 quick pulses: 쮜르르르)
    gain.gain.setValueAtTime(0, t);
    gain.gain.linearRampToValueAtTime(0.045, t + 0.03);
    gain.gain.linearRampToValueAtTime(0.01, t + 0.07);
    gain.gain.linearRampToValueAtTime(0.05, t + 0.11);
    gain.gain.linearRampToValueAtTime(0.01, t + 0.15);
    gain.gain.linearRampToValueAtTime(0.045, t + 0.19);
    gain.gain.linearRampToValueAtTime(0.0001, t + 0.28);

    osc.connect(gain);
    gain.connect(this.ambienceGain);

    osc.start(t);
    osc.stop(t + 0.3);
  }

  /**
   * Synthesize autumn dry leaf rustle (가을 낮 낙엽 바스락 소리: 사각/바스락)
   * Double filtered-noise friction bursts (1800Hz - 3600Hz)
   */
  public synthesizeLeafRustle(): void {
    if (!this.ctx || !this.ambienceGain || this.isMuted) return;

    const t = this.ctx.currentTime;
    const duration = 0.32;
    const bufferSize = Math.floor(this.ctx.sampleRate * duration);
    const noiseBuffer = this.ctx.createBuffer(1, bufferSize, this.ctx.sampleRate);
    const data = noiseBuffer.getChannelData(0);

    for (let i = 0; i < bufferSize; i++) {
      data[i] = (Math.random() * 2 - 1) * 0.35;
    }

    const noiseSource = this.ctx.createBufferSource();
    noiseSource.buffer = noiseBuffer;

    const highpass = this.ctx.createBiquadFilter();
    highpass.type = 'highpass';
    highpass.frequency.setValueAtTime(1800 + Math.random() * 300, t);

    const gain = this.ctx.createGain();
    // 2 subtle rustle friction envelopes: "바스-락"
    gain.gain.setValueAtTime(0, t);
    gain.gain.linearRampToValueAtTime(0.04, t + 0.04);
    gain.gain.linearRampToValueAtTime(0.01, t + 0.10);
    gain.gain.linearRampToValueAtTime(0.045, t + 0.18);
    gain.gain.exponentialRampToValueAtTime(0.0001, t + 0.30);

    noiseSource.connect(highpass);
    highpass.connect(gain);
    gain.connect(this.ambienceGain);

    noiseSource.start(t);
    noiseSource.stop(t + duration);
  }

  /**
   * Synthesize Thunderstorm Lightning Crack & Sub-bass Thunder Rumble (천둥소리)
   * 1. Ultra-fast high-frequency electric crack (0.04s)
   * 2. Heavy physical impact thud (50Hz - 80Hz)
   * 3. Long rolling sub-bass earthquake rumble (1.8s - 2.5s)
   */
  public synthesizeThunder(intensity = 1.0): void {
    if (!this.ctx || !this.ambienceGain || this.isMuted) return;

    const t = this.ctx.currentTime;
    const scaledIntensity = Math.max(0.2, Math.min(1.5, intensity));

    // 1. Lightning Crack (초단파 크랙 버스트: 콰쾅!)
    const crackDuration = 0.08;
    const crackBuffer = this.ctx.createBuffer(1, Math.floor(this.ctx.sampleRate * crackDuration), this.ctx.sampleRate);
    const crackData = crackBuffer.getChannelData(0);
    for (let i = 0; i < crackData.length; i++) {
      crackData[i] = (Math.random() * 2 - 1) * 0.8;
    }
    const crackSource = this.ctx.createBufferSource();
    crackSource.buffer = crackBuffer;

    const crackFilter = this.ctx.createBiquadFilter();
    crackFilter.type = 'highpass';
    crackFilter.frequency.setValueAtTime(1200, t);

    const crackGain = this.ctx.createGain();
    crackGain.gain.setValueAtTime(0, t);
    crackGain.gain.linearRampToValueAtTime(0.28 * scaledIntensity, t + 0.015);
    crackGain.gain.exponentialRampToValueAtTime(0.001, t + crackDuration);

    crackSource.connect(crackFilter);
    crackFilter.connect(crackGain);
    crackGain.connect(this.ambienceGain);
    crackSource.start(t);
    crackSource.stop(t + crackDuration);

    // 2. Heavy Sub-Bass Impact Thud (쿵!)
    const thudOsc = this.ctx.createOscillator();
    const thudGain = this.ctx.createGain();
    thudOsc.type = 'triangle';
    thudOsc.frequency.setValueAtTime(90, t);
    thudOsc.frequency.exponentialRampToValueAtTime(45, t + 0.35);

    thudGain.gain.setValueAtTime(0, t);
    thudGain.gain.linearRampToValueAtTime(0.32 * scaledIntensity, t + 0.03);
    thudGain.gain.exponentialRampToValueAtTime(0.001, t + 0.55);

    thudOsc.connect(thudGain);
    thudGain.connect(this.ambienceGain);
    thudOsc.start(t);
    thudOsc.stop(t + 0.6);

    // 3. Deep Rolling Rumble (쿠구구구구... 저역 럼블 감쇠)
    const rumbleDuration = 2.0;
    const rumbleBuffer = this.ctx.createBuffer(1, Math.floor(this.ctx.sampleRate * rumbleDuration), this.ctx.sampleRate);
    const rumbleData = rumbleBuffer.getChannelData(0);
    let last = 0;
    for (let i = 0; i < rumbleData.length; i++) {
      const white = Math.random() * 2 - 1;
      last = (last + 0.04 * white) / 1.04;
      rumbleData[i] = last * 2.2;
    }
    const rumbleSource = this.ctx.createBufferSource();
    rumbleSource.buffer = rumbleBuffer;

    const rumbleFilter = this.ctx.createBiquadFilter();
    rumbleFilter.type = 'lowpass';
    rumbleFilter.frequency.setValueAtTime(110, t);
    rumbleFilter.frequency.linearRampToValueAtTime(55, t + rumbleDuration);

    const rumbleGain = this.ctx.createGain();
    rumbleGain.gain.setValueAtTime(0, t);
    rumbleGain.gain.linearRampToValueAtTime(0.24 * scaledIntensity, t + 0.12);
    rumbleGain.gain.exponentialRampToValueAtTime(0.0001, t + rumbleDuration);

    rumbleSource.connect(rumbleFilter);
    rumbleFilter.connect(rumbleGain);
    rumbleGain.connect(this.ambienceGain);
    rumbleSource.start(t);
    rumbleSource.stop(t + rumbleDuration);
  }

  /**
   * Synthesize atmospheric dungeon water drop (던전 물방울 뚝 소리)
   */
  public synthesizeWaterDrip(): void {
    if (!this.ctx || !this.ambienceGain || this.isMuted) return;

    const t = this.ctx.currentTime;
    const osc = this.ctx.createOscillator();
    const gain = this.ctx.createGain();

    osc.type = 'sine';
    const freq = 1200 + Math.random() * 400;
    osc.frequency.setValueAtTime(freq, t);
    osc.frequency.exponentialRampToValueAtTime(freq * 1.6, t + 0.08);

    gain.gain.setValueAtTime(0, t);
    gain.gain.linearRampToValueAtTime(0.06, t + 0.015);
    gain.gain.exponentialRampToValueAtTime(0.0001, t + 0.15);

    osc.connect(gain);
    gain.connect(this.ambienceGain);

    osc.start(t);
    osc.stop(t + 0.18);
  }
}
